import Box from 'assets/components/box/Box';
import DataMap from 'assets/components/dataMap/DataMap';
import { addLog } from 'assets/components/feedback/Feedback';
import { useModalForm } from 'assets/components/form/modalForm/ModalForm';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import { inputStyles } from 'assets/components/inputs/Input';
import CheckWrapper from 'assets/components/inputs/wrappers/CheckWrapper';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import useApi from 'assets/hooks/api/useApi';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { HttpStatus } from 'config/Api.Config';
import { compact, isEmpty, uniq } from 'lodash';
import ClaimType from 'models/claims/claimType/Model.ClaimType';
import claimTypeDealerAutomationRuleDeleteApi from 'models/dealerManagement/claimTypeDealerAutomationRule/delete/Api.ClaimTypeDealerAutomationRule.Delete';
import AutomationReadonlySummary from 'module/automation/components/Automation.ReadonlySummary';
import AutomationRulesTable from 'module/automation/components/Automation.Rule.Table';
import { createRule } from 'module/automation/helpers/ruleHelpers';
import { createDealerClaimAutomationSchema } from 'module/automation/schemas/ClaimAutomationSchema';
import useClaimStatuses from 'module/claims/hooks/useClaimStatuses';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { DealerForm, useDealerProvider } from '../../Dealer.Hooks';

export default function DealerClaimTypeAutomationRules({
  additionalClaimTypeIds,
}: {
  additionalClaimTypeIds: number[];
}) {
  const { lang } = getLocales();
  const { form } = useModalForm<DealerForm>();
  const { selected } = useDealerProvider();
  const { automationClaimStatuses, automationClaimJobStatuses } = useClaimStatuses();
  const schema = createDealerClaimAutomationSchema({
    claimStatuses: automationClaimStatuses,
    claimJobStatuses: automationClaimJobStatuses,
  });

  const claimTypeIds = useMemo(() => {
    return uniq([...compact(selected?.dealerClaimTypes?.map((c) => c.claimTypeId)), ...additionalClaimTypeIds]);
  }, [selected?.dealerClaimTypes, additionalClaimTypeIds]);

  const { payload: claimTypesPayload } = useApi(
    {
      action: ClaimType.list,
      wait: !claimTypeIds.length,
      body: { id: claimTypeIds },
    },
    [claimTypeIds]
  );

  const updateRule = (id: number, newData: Partial<Model.IClaimTypeDealerAutomationRule>, claimTypeId: number) => {
    const updated = form.data?.claimTypeDealerAutomationRules?.map((entry) =>
      entry.claimTypeAutomationRuleId === id
        ? {
            ...entry,
            ...newData,
            claimTypeAutomationRule: {
              claimTypeId,
            },
          }
        : entry
    );
    form.update({ claimTypeDealerAutomationRules: updated });
  };

  const deleteRule = async (claimTypeRuleId: number, ruleId?: number, claimTypeId?: number) => {
    const updatedRules = form.data?.claimTypeDealerAutomationRules?.filter(
      (it) => it.claimTypeAutomationRuleId !== claimTypeRuleId
    );
    form.update({ claimTypeDealerAutomationRules: updatedRules });

    setOverrideClaimRule((prev) => ({
      ...prev,
      [claimTypeId]: false,
    }));

    if (ruleId) {
      const res = await claimTypeDealerAutomationRuleDeleteApi({ id: ruleId });
      if (res.status !== HttpStatus.ok) {
        addLog({ error: res.message });
      }
    }
  };

  const updateEnabled = (claimTypeId: number, newData: { isClaimTypeAutomationEnabled: any }) => {
    const currentDealerClaimTypes = [...(form.data.dealerClaimTypes || [])];
    const updated = currentDealerClaimTypes.map((entry) =>
      entry.claimTypeId === claimTypeId ? { ...entry, ...newData } : entry
    );
    form.update({ dealerClaimTypes: updated });
  };

  const [overrideClaimRule, setOverrideClaimRule] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (isEmpty(overrideClaimRule)) {
      const initialOverrides: Record<string, boolean> = {};

      claimTypesPayload?.data?.forEach((claimType) => {
        const automationRuleIds = claimType.claimTypeAutomationRules?.map((r) => r.id) ?? [];
        const hasDealerOverride = (form.data?.claimTypeDealerAutomationRules ?? []).some((dealerRule) =>
          automationRuleIds.includes(dealerRule.claimTypeAutomationRuleId)
        );

        if (hasDealerOverride) initialOverrides[claimType.id] = hasDealerOverride;
      });

      setOverrideClaimRule(initialOverrides);
    }
  }, [claimTypesPayload?.data, form.data?.claimTypeDealerAutomationRules]);

  return (
    <FormWrapper>
      <DataMap
        data={claimTypesPayload?.data}
        render={({ data: claimType }) => {
          const dealerClaimType = form.data?.dealerClaimTypes?.find((it) => it.claimTypeId === claimType.id);

          return (
            !!claimType.claimTypeAutomationRules.length &&
            dealerClaimType && (
              <Fragment key={claimType.id}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: Theme.Size.M }}>
                    <h3>{claimType.name}</h3>
                    <CheckWrapper>
                      <CheckInput
                        name="isClaimTypeAutomationEnabled"
                        label={lang.enabled}
                        value={dealerClaimType?.isClaimTypeAutomationEnabled}
                        onChange={({ isClaimTypeAutomationEnabled }) =>
                          updateEnabled(claimType.id, { isClaimTypeAutomationEnabled })
                        }
                        class={inputStyles.check.switch}
                        htmlElementProps={{
                          title: dealerClaimType?.isClaimTypeAutomationEnabled ? lang.disable : lang.enable,
                        }}
                      />
                    </CheckWrapper>
                  </div>
                </div>
                &nbsp;
                {dealerClaimType?.isClaimTypeAutomationEnabled && (
                  <DataMap
                    data={claimType.claimTypeAutomationRules}
                    render={({ data: rule }) => {
                      const currentRule = form.data?.claimTypeDealerAutomationRules?.find(
                        (c) => c.claimTypeAutomationRuleId === rule.id
                      );

                      return (
                        <div key={rule.id}>
                          <Box style={{ display: 'flex', flexDirection: 'column', marginBottom: '1rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                              <h3>{lang.claimRule}</h3>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: Theme.Size.M }}>
                              <AutomationReadonlySummary rule={rule} schema={schema} />
                              <div style={{ alignSelf: 'flex-start' }}>
                                <CheckWrapper>
                                  <CheckInput
                                    name="overrideClaimRule"
                                    label={lang.overrideClaimRule}
                                    value={overrideClaimRule[claimType.id]}
                                    onChange={({ overrideClaimRule: isOverrided }) => {
                                      setOverrideClaimRule((prev) => ({ ...prev, [claimType.id]: isOverrided }));
                                      const currentRules = form.data?.claimTypeDealerAutomationRules || [];

                                      if (isOverrided) {
                                        const alreadyExists = currentRules.some(
                                          (it) => it.claimTypeAutomationRuleId === rule.id
                                        );
                                        if (alreadyExists) return;

                                        const newEntry = createRule(schema, {
                                          claimTypeId: claimType.id,
                                          claimTypeAutomationRuleId: rule.id,
                                          claimTypeAutomationRule: {
                                            claimTypeId: claimType.id,
                                          },
                                        }) as Model.IClaimTypeDealerAutomationRule;

                                        form.update({ claimTypeDealerAutomationRules: [...currentRules, newEntry] });
                                      } else {
                                        const filtered = currentRules.filter(
                                          (r) => r.claimTypeAutomationRuleId !== rule.id
                                        );
                                        form.update({ claimTypeDealerAutomationRules: filtered });
                                      }
                                    }}
                                    disabled={(form.data?.claimTypeDealerAutomationRules ?? []).some(
                                      (it) => it.claimTypeAutomationRuleId === rule.id && it.id
                                    )}
                                    class={inputStyles.check.radio}
                                  />
                                </CheckWrapper>
                              </div>
                            </div>
                          </Box>
                          {overrideClaimRule[claimType.id] && currentRule && (
                            <div>
                              <h3>{lang.overrideClaimRule}</h3>
                              <AutomationRulesTable<Model.IClaimTypeDealerAutomationRule>
                                value={[currentRule as Model.IClaimTypeDealerAutomationRule]}
                                schema={schema}
                                hideAddRule
                                onChange={(rules) => updateRule(rule.id, rules[0], claimType.id)}
                                onDeleteRule={() => deleteRule(rule.id, currentRule?.id, claimType?.id)}
                              />
                            </div>
                          )}
                        </div>
                      );
                    }}
                  />
                )}
                <br />
              </Fragment>
            )
          );
        }}
      />
    </FormWrapper>
  );
}
